import React, { useState } from 'react';
import { Camera, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import DoorPhotos, { CHECKOUT_SHOTS, MAX_PHOTOS } from './DoorPhotos';
import { uploadOrderPhotos, track } from '../../lib/shopApi';

// The photo ask, after the money (Hugh, 17 Sep 2026): the customer pays first, then this
// block asks for the three shots a locksmith needs to check the fit and read the size — the
// backset is never asked before payment. It sits on the success page and, for the email
// link, on /shop/order/photos. `token` is the order's photoToken; `received` is how many are
// already on the order (a return visit); `next` is what the photos unblock ("book a time"
// for an install, "send it" for a shipped lock).
export default function OrderPhotosUpload({ orderNo, token, received = 0, next = 'book a time', onSent, testId = 'order-photos' }) {
  const [photos, setPhotos] = useState([]);
  const [measured, setMeasured] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(0);
  const [more, setMore] = useState(false);
  const room = Math.max(0, MAX_PHOTOS - received - sent);

  const send = async () => {
    setError('');
    // Two is the minimum a locksmith can call the fit from; a return visit only tops up.
    if (photos.length + sent + received < 2) {
      setError('Add at least two photos — the door edge with the tape, and the inside of the door.');
      return;
    }
    setBusy(true);
    try {
      const batch = photos.slice(0, room);
      const mm = Number(measured);
      const res = await uploadOrderPhotos(orderNo, token, batch, mm >= 10 && mm <= 150 ? Math.round(mm) : null);
      setSent((n) => n + batch.length);
      setPhotos([]);
      setMore(false);
      track('order_photos_sent', { order: orderNo, photos: batch.length, measured: mm > 0 });
      if (onSent) onSent(res);
    } catch (err) {
      setError(err.message || 'Could not send the photos — reply to your confirmation email with them instead.');
    } finally {
      setBusy(false);
    }
  };

  if (sent > 0 && !more) {
    return (
      <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-800" role="status" data-testid={`${testId}-sent`}>
        <p className="flex items-start gap-2 font-semibold"><CheckCircle className="mt-0.5 shrink-0" size={18} /> Got them — {sent + received} photo{sent + received === 1 ? '' : 's'} on order {orderNo}.</p>
        <p className="text-sm mt-2">We check the fit and {next} within one working day.</p>
        {room > 0 && (
          <button type="button" onClick={() => setMore(true)} className="text-sm underline mt-2">Add another photo</button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-5 sm:p-6" data-testid={testId}>
      <p className="flex items-center gap-2 text-xl font-bold text-gray-900"><Camera size={22} className="text-red-600" /> One more thing — three photos of the door, then we {next}.</p>
      {received > 0 && <p className="text-sm text-gray-700 mt-1">{received} already received. Add up to {room} more.</p>}
      <div className="mt-4">
        <DoorPhotos photos={photos} onChange={setPhotos} shots={CHECKOUT_SHOTS} testId={`${testId}-picker`} />
      </div>
      <label className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
        Got a tape on it? Edge to the centre of the keyhole
        <input type="number" inputMode="numeric" min="10" max="150" value={measured} onChange={(e) => setMeasured(e.target.value)} className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-gray-900 bg-white" aria-label="Edge to lock centre in mm" data-testid={`${testId}-measured`} />
        mm <span className="text-gray-500">(optional)</span>
      </label>
      {error && <p className="text-sm text-red-700 mt-3" role="alert">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Button onClick={send} disabled={busy || !photos.length || room === 0} className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid={`${testId}-send`}>
          {busy ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Camera className="mr-2" size={18} />}
          Send photos
        </Button>
        <span className="text-sm text-gray-600">Not now? The link is in your confirmation email.</span>
      </div>
    </div>
  );
}
